package ai.wagemix.logic.sharedmix

import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.data.WMRemoteException
import ai.wagemix.data.WMSharedLinkDeactivatedException
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.session
import ai.wagemix.logic.sharedmix.WMSharedMixPresenter.Action
import ai.wagemix.logic.sharedmix.WMSharedMixPresenter.Event
import ai.wagemix.logic.wages.WM_DEFAULT_PROJECTED_YEARS
import ai.wagemix.logic.wages.toWageSummary
import ai.wagemix.logic.wages.WMWageSummary
import ai.wagemix.net.models.firebase.WMSharedMix
import ai.wagemix.net.models.WMBadge
import ai.wagemix.net.models.WMSharedMixPurpose
import com.fsryan.foundation.logic.FSBasePresenter
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.time.Clock
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.coroutines.flow.onCompletion
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.take
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** Why the page has no mix to show; [Deactivated]: the owner turned the link off (share-link-stats-plan). */
enum class WMSharedMixError { InvalidLink, NotFound, Deactivated, Failed }

private data class Optional<T>(val value: T?)

data class WMSharedOccupation(val occupationId: Int, val title: String, val share: Double)

/** Everything the public page renders. */
data class WMSharedMixPresentation(
    val title: String,
    val hourly: Boolean,
    val badge: WMBadge,
    val payRangeLow: Int?,
    val payRangeHigh: Int?,
    val purpose: WMSharedMixPurpose,
    val areaName: String?,
    val industryName: String?,
    val occupations: List<WMSharedOccupation>,
    val summary: WMWageSummary,
    val previewImageUrl: String?
)

/** The public shared-mix page: loads the document, projects to the current year, and renders. */
interface WMSharedMixPresenter<out E: Event, in A: Action>: FSPresenter<E, A> {

    interface Action: FSPresenter.Action {
        sealed interface Base: Action

        interface Factory<out A: Action>: FSPresenter.Action.Factory<A>

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {}
        }
    }

    interface Event: FSPresenter.Event {
        val mixUpdate: WMSharedMixPresentation?
        val errorUpdate: WMSharedMixError?

        @ConsistentCopyVisibility
        data class Base internal constructor(
            private val base: FSPresenter.Event.Base,
            override val mixUpdate: WMSharedMixPresentation? = null,
            override val errorUpdate: WMSharedMixError? = null
        ): Event, FSPropertyContainer<Base> {
            override val loadingUpdate: Boolean? get() = base.loadingUpdate
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<out E: Event>: FSPresenter.Event.Factory<E> {
            fun createMixUpdate(mix: WMSharedMixPresentation): E
            fun createError(error: WMSharedMixError): E
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                private val base = FSPresenter.Event.baseFactory()
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createMixUpdate(mix: WMSharedMixPresentation) = Base(base.none(), mixUpdate = mix)
                override fun createError(error: WMSharedMixError) = Base(base.none(), errorUpdate = error)
            }
        }
    }

    companion object
}

abstract class WMBaseSharedMixPresenter<
    E: Event,
    A: Action,
    EF: Event.Factory<E>,
    G: WMSharedMixDataGateway
>(
    protected val sharedMixId: String?,
    protected val dataGateway: G,
    eventFactory: EF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    protected val currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year }
): FSBasePresenter<E, A, EF>(cancelViewScopeOnUnready, instanceId, eventFactory), WMSharedMixPresenter<E, A> {

    override fun registerSubscriptions() {
        val id = sharedMixId?.takeIf { it.isNotBlank() } ?: return
        // The document is mirrored into the global context while this presenter observes it.
        dataGateway.contextUpdateFlow
            .filter { it.isGlobal && it.sharedMixChanged(id) }
            .map { Optional(it.after.sharedMix(id)) }
            .onStart { emit(Optional(dataGateway.retrieveGlobalContext().sharedMix(id))) }
            .onEach { (shared) ->
                if (shared != null) {
                    eventFactory.createLoadingChange(false).emit()
                    eventFactory.createMixUpdate(shared.toPresentation()).emit()
                }
            }
            .onCompletion { dataGateway.stopObservingSharedMix(id) }
            .launchInViewScope()

        // `shared_mix_viewed` once per attach, when the document first shows.
        dataGateway.contextUpdateFlow
            .filter { it.isGlobal && it.sharedMixChanged(id) }
            .map { Optional(it.after.sharedMix(id)) }
            .onStart { emit(Optional(dataGateway.retrieveGlobalContext().sharedMix(id))) }
            .mapNotNull { it.value }
            .take(1)
            .onEach { shared ->
                FSLog.sendWMSharedMixViewed(mixId = id, badgeTier = shared.badge, viewerIsOwner = viewerOwns(id))
            }
            .launchInViewScope()

        dataGateway.mixSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMMixSyncEvent.ObserveSharedMix.Failure -> {
                        eventFactory.createLoadingChange(false).emit()
                        val error = event.error
                        val kind = when {
                            error is WMSharedLinkDeactivatedException -> WMSharedMixError.Deactivated
                            error is WMRemoteException && error.isNotFound -> WMSharedMixError.NotFound
                            else -> WMSharedMixError.Failed
                        }
                        eventFactory.createError(kind).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    /** Whether the signed-in viewer's own share results (made on this device) include [sharedMixId]. */
    private suspend fun viewerOwns(sharedMixId: String): Boolean {
        val uid = dataGateway.session()?.uid ?: return false
        val context = dataGateway.retrieveUserContext(uid)
        return context.storedMixes()?.any { context.shareResult(it.id)?.sharedMixId == sharedMixId } == true
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        val id = sharedMixId?.takeIf { it.isNotBlank() }
        when (id) {
            null -> eventFactory.createError(WMSharedMixError.InvalidLink).emit()
            else -> {
                eventFactory.createLoadingChange(true).emit()
                dataGateway.observeSharedMix(id, createPresenterContext())
            }
        }
    }

    override fun onUserAction(action: A) {
        logUserAction(action)
        logUnknownActionType(action)
    }

    private fun WMSharedMix.toPresentation(): WMSharedMixPresentation {
        val hourly = mode == "hourly"
        val totalPercentage = occupations.filter { it.isEnabled }.sumOf { it.percentage }.takeIf { it > 0 } ?: 1.0
        val titles = data.orderedStats().associate { (id, s) -> id to s.title }
        return WMSharedMixPresentation(
            title = title,
            hourly = hourly,
            badge = badge,
            payRangeLow = payRange?.low,
            payRangeHigh = payRange?.high,
            purpose = purpose,
            areaName = filters.area,
            industryName = filters.industry,
            occupations = occupations.filter { it.isEnabled }
                .map { WMSharedOccupation(it.id, titles[it.id] ?: "", it.percentage / totalPercentage) }
                .sortedByDescending { it.share },
            summary = data.toWageSummary(hourly = hourly, projectedYears = WM_DEFAULT_PROJECTED_YEARS, currentYear = currentYear()),
            previewImageUrl = previewImageUrl
        )
    }
}

typealias WMDefaultSharedMixPresenter = WMSharedMixPresenter<Event.Base, Action.Base>

fun WMSharedMixPresenter.Companion.createDefault(
    sharedMixId: String?,
    dataGateway: WMSharedMixDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMSharedMixPresenter"),
    currentYear: () -> Int = { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year }
): WMDefaultSharedMixPresenter = object: WMBaseSharedMixPresenter<Event.Base, Action.Base, Event.Factory<Event.Base>, WMSharedMixDataGateway>(
    sharedMixId = sharedMixId,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId,
    currentYear = currentYear
) {}
